import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Plus, Trash2, ClipboardList, MessageCircle, Pencil, FileDown, FileUp, X, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { fmtARS, apiError } from "@/lib/api";
import { useQuoteEditor } from "@/context/QuoteEditorContext";

const BADGE = {
  borrador: "bg-neutral-700/30 text-neutral-300 border-neutral-700",
  enviada: "bg-blue-500/15 text-blue-400 border-blue-500/40",
  aprobada: "bg-green-500/15 text-green-400 border-green-500/40",
  rechazada: "bg-red-500/15 text-red-400 border-red-500/40",
};

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [materialSearch, setMaterialSearch] = useState("");
  const { editing, setEditing, editId, openEdit, closeEdit, clearDraft } = useQuoteEditor();
  const [versionsOf, setVersionsOf] = useState(null);
  const location = useLocation();

  const openVersions = async (q) => {
    try {
      const { data } = await api.get(`/quotes/${q.id}/versions`);
      setVersionsOf({ number: q.number, list: data });
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadVersionPdf = async (v) => {
    try {
      const res = await api.get(`/quotes/${v.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Cotizacion-${v.number}-v${v.version || 1}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = useCallback(() => {
    api.get("/quotes").then((r) => {
      setQuotes(r.data);
      const openId = location.state?.openQuote;
      if (location.state?.newQuote) {
        openEdit(null);
        window.history.replaceState({}, "");
        return;
      }
      if (openId) {
        const q = r.data.find((x) => x.id === openId);
        if (q) openEdit(q);
        window.history.replaceState({}, "");
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {});
  }, [load]);

  const setItem = (idx, key, value) => {
    const items = editing.items.map((it, i) => (i === idx ? { ...it, [key]: value } : it));
    setEditing({ ...editing, items });
  };

  const addMaterialItem = (matId) => {
    const m = materials.find((x) => x.id === matId);
    if (!m) return;
    setEditing({ ...editing, items: [...editing.items, { description: `${m.name} (${m.unit})`, quantity: 1, unit_price: m.price }] });
    setMaterialSearch("");
  };

  const totals = () => {
    if (!editing) return { mat: 0, sub: 0, total: 0 };
    const mat = editing.items.reduce((a, i) => a + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);
    const sub = mat + Number(editing.labor_cost || 0);
    return { mat, sub, total: sub * (1 + Number(editing.margin_pct || 0) / 100) };
  };

  const filteredMaterials = () => {
    const words = materialSearch.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const list = words.length
      ? materials.filter((m) => {
          const haystack = [m.name, m.category, m.source_group, m.source_code, m.unit].filter(Boolean).join(" ").toLowerCase();
          return words.every((w) => haystack.includes(w));
        })
      : materials;
    return list.slice(0, 30);
  };

  const save = async () => {
    if (!editing.client_name) { toast.error("Ingresá el nombre del cliente"); return; }
    const payload = {
      ...editing,
      labor_cost: Number(editing.labor_cost || 0),
      margin_pct: Number(editing.margin_pct || 0),
      items: editing.items.map((i) => ({ description: i.description, quantity: Number(i.quantity || 0), unit_price: Number(i.unit_price || 0) })),
    };
    try {
      if (editId) await api.put(`/quotes/${editId}`, payload);
      else await api.post("/quotes", payload);
      toast.success("Cotización guardada");
      clearDraft();
      closeEdit();
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/quotes/${id}/status`, { status }); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const toOrder = async (id) => {
    try {
      const { data } = await api.post(`/quotes/${id}/to-order`);
      toast.success(`Orden de trabajo ${data.number} creada`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (q) => {
    if (!window.confirm(`Eliminar la cotización ${q.number} definitivamente? Si tiene una orden asociada, tambien se elimina esa orden y sus planes de corte.`)) return;
    try {
      await api.delete(`/quotes/${q.id}`);
      load();
      toast.success("Cotización eliminada");
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadPdf = async (q) => {
    try {
      const res = await api.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Cotizacion-${q.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendPdf = async (q) => {
    const client = clients.find((c) => c.id === q.client_id);
    if (!client?.phone) { toast.error("El cliente no tiene teléfono cargado"); return; }
    try {
      const res = await api.post(`/quotes/${q.id}/send-pdf`, {
        phone: client.phone,
        message: `Hola ${q.client_name}! Te enviamos la cotización ${q.number} por ${fmtARS(q.total)}. GR Soluciones Integrales.`,
        client_id: q.client_id,
      });
      toast.success("PDF enviado por WhatsApp");
      if (q.status === "borrador") setStatus(q.id, "enviada");
      return res;
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendWhatsApp = async (q) => {
    const client = clients.find((c) => c.id === q.client_id);
    if (!client?.phone) { toast.error("El cliente no tiene teléfono cargado"); return; }
    try {
      const tpls = (await api.get("/whatsapp/templates")).data;
      const tpl = tpls.find((t) => t.name.toLowerCase().includes("cotiza"));
      const res = await api.post("/whatsapp/send", {
        phone: client.phone,
        template_id: tpl?.id,
        message: tpl ? undefined : `Hola ${q.client_name}! Te enviamos la cotización ${q.number} por ${fmtARS(q.total)}. GR Soluciones Integrales.`,
        variables: { nombre: q.client_name, numero: q.number, total: String(q.total) },
        client_id: q.client_id,
      });
      toast.success(res.data.mock ? "Mensaje registrado (modo simulado — Evolution API no configurada)" : "Mensaje enviado por WhatsApp");
      if (q.status === "borrador") setStatus(q.id, "enviada");
    } catch (e) { toast.error(apiError(e)); }
  };

  const t = totals();

  return (
    <div data-testid="quotes-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-bold uppercase text-2xl tracking-tight">Cotizaciones</h1>
        <Button onClick={() => openEdit(null)} className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase text-xs" data-testid="new-quote-btn">
          <Plus className="w-4 h-4 mr-1" /> Nueva cotización
        </Button>
      </div>

      {quotes.length === 0 && <p className="text-neutral-500 text-sm">Todavía no hay cotizaciones. Crealas desde una consulta o con el botón "Nueva cotización".</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {quotes.map((q) => (
          <div key={q.id} className="bg-[#171717] border border-neutral-800 rounded-md p-5 hover:border-neutral-700 transition-colors flex flex-col" data-testid={`quote-card-${q.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <span className="font-mono text-sm text-[#F2B90D] flex items-center gap-1.5">
                {q.number}
                {q.version > 1 && (
                  <button onClick={() => openVersions(q)} className="text-[10px] font-bold border border-[#F2B90D]/40 rounded-sm px-1.5 py-0.5 hover:bg-[#F2B90D]/10 flex items-center gap-1" title="Ver versiones anteriores" data-testid={`quote-versions-${q.id}`}>
                    <History className="w-3 h-3" /> v{q.version}
                  </button>
                )}
              </span>
              <select value={q.status} onChange={(e) => setStatus(q.id, e.target.value)}
                className={`text-[11px] uppercase font-bold px-2 py-1 rounded-sm border bg-transparent shrink-0 ${BADGE[q.status]}`} data-testid={`quote-status-select-${q.id}`}>
                {Object.keys(BADGE).map((s) => <option key={s} value={s} className="bg-neutral-900 text-white">{s}</option>)}
              </select>
            </div>
            <p className="text-sm font-medium truncate">{q.client_name}</p>
            <p className="text-xs text-neutral-500 truncate">{q.title || "Sin título"}</p>
            <p className="font-mono text-xl font-bold mt-3">{fmtARS(q.total)}</p>

            <div className="flex gap-2 mt-4 pt-3 border-t border-neutral-800">
              <Button size="sm" variant="outline" onClick={() => openEdit(q)} className="flex-1 border-neutral-700 rounded-sm h-8 px-2 hover:bg-neutral-800 hover:text-white" title="Editar" data-testid={`quote-edit-${q.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => downloadPdf(q)} className="flex-1 border-neutral-700 rounded-sm h-8 px-2 hover:bg-neutral-800 hover:text-white" title="Descargar PDF" data-testid={`quote-pdf-${q.id}`}>
                <FileDown className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => sendPdf(q)} className="flex-1 border-[#25D366]/50 text-[#25D366] rounded-sm h-8 px-2 hover:bg-[#25D366]/10 hover:text-[#25D366]" title="Enviar PDF por WhatsApp" data-testid={`quote-send-pdf-${q.id}`}>
                <FileUp className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => sendWhatsApp(q)} className="flex-1 border-[#25D366]/50 text-[#25D366] rounded-sm h-8 px-2 hover:bg-[#25D366]/10 hover:text-[#25D366]" title="Enviar por WhatsApp" data-testid={`quote-whatsapp-${q.id}`}>
                <MessageCircle className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => remove(q)} className="flex-1 border-neutral-700 text-red-400 rounded-sm h-8 px-2 hover:bg-red-500/10 hover:text-red-400" title="Eliminar cotización" data-testid={`quote-delete-${q.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
            <Button onClick={() => toOrder(q.id)} className="w-full mt-2 bg-neutral-800 hover:bg-neutral-700 rounded-sm h-8 text-xs font-heading uppercase" title="Convertir en orden de trabajo" data-testid={`quote-to-order-${q.id}`}>
              <ClipboardList className="w-3.5 h-3.5 mr-1" /> A orden
            </Button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 md:left-64 top-16 md:top-0 z-20 bg-[#171717] overflow-y-auto p-4 sm:p-6" data-testid="quote-editor-panel">
          <div className="sticky top-0 z-10 flex items-center justify-between -mx-4 -mt-4 px-4 py-4 sm:-mx-6 sm:-mt-6 sm:px-6 bg-[#171717] border-b border-neutral-800">
            <h2 className="font-heading uppercase font-bold">{editId ? "Editar cotización" : "Nueva cotización"}</h2>
            <button onClick={closeEdit} className="p-2 rounded-sm text-neutral-400 hover:bg-neutral-800 hover:text-white" aria-label="Cerrar" data-testid="quote-editor-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          {editing && (
            <div className="space-y-5 w-full max-w-7xl mx-auto pt-5 pb-8">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Cliente</label>
                  <select value={editing.client_id || ""} onChange={(e) => {
                    const c = clients.find((x) => x.id === e.target.value);
                    setEditing({ ...editing, client_id: c?.id || null, client_name: c?.name || editing.client_name });
                  }} className="w-full h-10 bg-neutral-900 border border-neutral-800 rounded-sm px-3 text-sm" data-testid="quote-client-select">
                    <option value="">— Escribir manualmente —</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Nombre del cliente</label>
                  <Input value={editing.client_name} onChange={(e) => setEditing({ ...editing, client_name: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="quote-client-name-input" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Título del trabajo</label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Ej: Reja frente 8m" className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="quote-title-input" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase font-bold text-neutral-400">Ítems (materiales / trabajos)</label>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...editing, items: [...editing.items, { description: "", quantity: 1, unit_price: 0 }] })}
                      className="border-neutral-700 rounded-sm h-8 text-xs hover:bg-neutral-800 hover:text-white" data-testid="quote-add-item-btn">
                      <Plus className="w-3 h-3 mr-1" /> Ítem
                    </Button>
                  </div>
                </div>
                <div className="relative mb-3">
                  <Input
                    value={materialSearch}
                    onChange={(e) => setMaterialSearch(e.target.value)}
                    placeholder="Buscar material: 60x40, chapa, angulo..."
                    className="bg-neutral-900 border-neutral-800 rounded-sm h-10"
                    data-testid="quote-material-search"
                  />
                  {materialSearch && (
                    <div className="absolute z-40 mt-1 w-full max-h-72 overflow-y-auto bg-neutral-950 border border-neutral-800 rounded-sm shadow-xl">
                      {filteredMaterials().length === 0 ? (
                        <p className="px-3 py-2 text-xs text-neutral-500">Sin resultados</p>
                      ) : filteredMaterials().map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => addMaterialItem(m.id)}
                          className="w-full text-left px-3 py-2 text-xs text-neutral-200 hover:bg-neutral-800"
                          data-testid={`quote-add-material-${m.id}`}
                        >
                          <span className="block font-medium truncate">{m.name}</span>
                          <span className="block font-mono text-neutral-500">{fmtARS(m.price)} · {m.unit || "unidad"} {m.source_group ? `· ${m.source_group}` : ""}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2 min-w-0">
                  {editing.items.map((it, idx) => (
                    <div key={idx} className="grid grid-cols-1 sm:grid-cols-[minmax(180px,1fr)_90px_140px_120px_32px] gap-2 items-center bg-neutral-900/40 sm:bg-transparent p-2 sm:p-0 rounded-sm">
                      <Input value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} placeholder="Descripción" className="bg-neutral-900 border-neutral-800 rounded-sm min-w-0 h-9" data-testid={`quote-item-desc-${idx}`} />
                      <Input type="number" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} placeholder="Cantidad" className="bg-neutral-900 border-neutral-800 rounded-sm w-full h-9" data-testid={`quote-item-qty-${idx}`} />
                      <Input type="number" value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} placeholder="Precio" className="bg-neutral-900 border-neutral-800 rounded-sm w-full h-9" data-testid={`quote-item-price-${idx}`} />
                      <span className="font-mono text-xs sm:text-right text-neutral-400">{fmtARS(Number(it.quantity || 0) * Number(it.unit_price || 0))}</span>
                      <button onClick={() => setEditing({ ...editing, items: editing.items.filter((_, i) => i !== idx) })} className="text-neutral-500 hover:text-red-400 justify-self-end sm:justify-self-center" data-testid={`quote-item-delete-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {editing.items.length === 0 && <p className="text-xs text-neutral-500">Agregá ítems manuales o desde tu lista de materiales.</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Mano de obra ($)</label>
                  <Input type="number" value={editing.labor_cost} onChange={(e) => setEditing({ ...editing, labor_cost: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="quote-labor-input" />
                </div>
                <div>
                  <label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Margen (%)</label>
                  <Input type="number" value={editing.margin_pct} onChange={(e) => setEditing({ ...editing, margin_pct: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="quote-margin-input" />
                </div>
              </div>

              <div className="bg-neutral-900 border border-neutral-800 rounded-sm p-4 space-y-1 text-sm">
                <div className="flex justify-between text-neutral-400"><span>Materiales</span><span className="font-mono">{fmtARS(t.mat)}</span></div>
                <div className="flex justify-between text-neutral-400"><span>Subtotal + mano de obra</span><span className="font-mono">{fmtARS(t.sub)}</span></div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-neutral-800"><span className="font-heading uppercase">Total</span><span className="font-mono text-[#F2B90D]" data-testid="quote-total">{fmtARS(t.total)}</span></div>
              </div>

              <Button onClick={save} className="w-full bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase" data-testid="quote-save-btn">Guardar cotización</Button>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!versionsOf} onOpenChange={(o) => !o && setVersionsOf(null)}>
        <DialogContent className="bg-[#171717] border-neutral-800 text-white">
          <DialogHeader><DialogTitle className="font-heading uppercase">Versiones de {versionsOf?.number}</DialogTitle></DialogHeader>
          {versionsOf && (
            <div className="space-y-2">
              {versionsOf.list.map((v) => (
                <div key={v.id} className="bg-neutral-900 border border-neutral-800 rounded-sm px-3 py-2 flex items-center justify-between gap-2" data-testid={`version-row-${v.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm">v{v.version || 1}{v.is_latest !== false ? " · actual" : ""}</p>
                    <p className="text-[11px] text-neutral-500">{new Date(v.created_at).toLocaleString("es-AR")} · {fmtARS(v.total)}</p>
                  </div>
                  <button onClick={() => downloadVersionPdf(v)} className="text-neutral-400 hover:text-[#F2B90D] p-1.5" title="Descargar PDF de esta versión" data-testid={`version-pdf-${v.id}`}>
                    <FileDown className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
